import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Loader2, Map as MapIcon } from "lucide-react";
import api from "@/lib/api";
import LeafletMap from "./LeafletMap";
import { useOsMe, OsDenied } from "./osShared";

const LAYER_COLORS = ["#b45309", "#047857", "#7c3aed", "#be123c", "#0e7490"];

const NATIONAL_WMS = [
  { id: "naturreservat", name: "Naturreservat", color: "#15803d", url: "https://geodata.naturvardsverket.se/naturvardsregistret/wms", layers: "Naturreservat,Naturreservat_kommunalt,Nationalpark", attribution: "© Naturvårdsverket" },
  { id: "natura2000", name: "Natura 2000", color: "#65a30d", url: "https://geodata.naturvardsverket.se/inspire/ps-n2k/ows", layers: "PS.ProtectedSites.Natura2000", attribution: "© Naturvårdsverket" },
  { id: "fornlamningar", name: "Fornlämningar", color: "#9a3412", url: "https://pub.raa.se/visning/lamningar_v1/wms", layers: "fornlamning,mojligfornlamning", attribution: "© Riksantikvarieämbetet" },
  { id: "strandskydd", name: "Strandskydd", color: "#0369a1", url: "https://ext-geodata.lansstyrelsen.se/arcgis/services/WMS/LSTO_vms_strandskydd/MapServer/WMSServer", layers: "0", attribution: "© Länsstyrelserna" },
];

export default function OsMapView() {
  const { denied } = useOsMe();
  const [properties, setProperties] = useState(null);
  const [layers, setLayers] = useState([]);
  const [wms, setWms] = useState(NATIONAL_WMS.map((w) => ({ ...w, visible: false })));

  useEffect(() => {
    api.get("/os/properties?limit=200").then((r) => setProperties(r.data.items)).catch(() => setProperties([]));
    api.get("/os/spatial-layers?limit=100").then(async (r) => {
      const ls = await Promise.all(
        r.data.items.map(async (l, i) => {
          const f = await api.get(`/os/spatial-features?layerId=${l.id}&limit=200`).catch(() => ({ data: { items: [] } }));
          return { id: l.id, name: l.name, color: LAYER_COLORS[i % LAYER_COLORS.length], features: f.data.items, visible: true };
        })
      );
      setLayers(ls);
    }).catch(() => {});
  }, []);

  if (denied) return <OsDenied />;
  if (!properties) return <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;

  const markers = properties
    .filter((p) => p.geometry?.type === "Point")
    .map((p) => ({ lat: p.geometry.coordinates[1], lng: p.geometry.coordinates[0], label: p.designation, href: "/os/properties" }));

  return (
    <div className="max-w-6xl mx-auto animate-fade-in-up" data-testid="os-map-page">
      <Link to="/os" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-blue-700">
        <ChevronLeft className="w-4 h-4" /> Ärenden
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center rounded-sm"><MapIcon className="w-5 h-5 stroke-[1.5]" /></div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Bygglovs-OS · Karta</h1>
          <p className="text-xs text-zinc-500">{markers.length} fastigheter · {layers.length} spatiala lager</p>
        </div>
      </div>

      <div className="mt-4" data-testid="os-map-wms-toggles">
        <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium mb-1.5">Nationella kartlager</p>
        <div className="flex flex-wrap gap-2">
          {wms.map((w) => (
            <label key={w.id} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-sm cursor-pointer transition-colors ${w.visible ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:border-zinc-500"}`}>
              <input type="checkbox" className="sr-only" checked={w.visible} data-testid={`os-wms-toggle-${w.id}`}
                onChange={() => setWms(wms.map((x) => x.id === w.id ? { ...x, visible: !x.visible } : x))} />
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: w.color }} /> {w.name}
            </label>
          ))}
        </div>
      </div>

      {layers.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="os-map-layer-toggles">
          {layers.map((l) => (
            <label key={l.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-zinc-300 rounded-sm cursor-pointer">
              <input type="checkbox" checked={l.visible} data-testid={`os-layer-toggle-${l.id}`}
                onChange={() => setLayers(layers.map((x) => x.id === l.id ? { ...x, visible: !x.visible } : x))} />
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: l.color }} /> {l.name}
            </label>
          ))}
        </div>
      )}

      <div className="mt-4">
        <LeafletMap markers={markers} layers={layers} wmsLayers={wms} height="h-[560px]" />
      </div>
    </div>
  );
}
